/**
 * @file `AdminTerminalSub` — `SubscriptionContract` for the admin
 *       console's read-only terminal-attach WS. Shared between the
 *       admin-side proxy handler (`bindWebSocket`) and the SPA's
 *       terminal Client (`WsClientTransport.subscribe`).
 * @module @labcraft/admin/shared/terminal-subscription
 *
 * The admin console attaches to a running Machine's pty purely to
 * observe it — the operator sees the learner's live terminal but never
 * types into it. So the browser-facing contract is asymmetric:
 *
 *   - **Binary** (pty stdout bytes) flows server → browser via
 *     `sendBinary` / `onBinary`, unvalidated (opaque bytes). The proxy
 *     drops browser → server binary (operator keystrokes) rather than
 *     forwarding them upstream.
 *   - **Text JSON**:
 *     - Client → Server: only `resume` — the framework's
 *       `WsSubscription` auto-sends it on reconnect. The proxy ignores
 *       it (read-only); it exists in the contract solely so the typed
 *       client's outbound validation accepts the auto-resume.
 *     - Server → Client: `lifecycle` (the state pill) and `resize`
 *       (the learner's current pty window size, mirrored so the admin
 *       drawer tracks the learner's dimensions live). Both are relayed
 *       verbatim from the hypervisor's `PtyServerFrame`.
 *
 * The frame schemas are redeclared here rather than imported from
 * `@labcraft/hypervisor` — `src/shared/` lives in the admin UI closure,
 * and importing the hypervisor workspace would pull its Firecracker
 * backend into the SPA bundle. Same boundary-redeclaration discipline
 * as `hypervisor_routes.ts` (this workspace's mirror of the wire
 * `Machine`).
 */

import { defineSubscription, CoreString } from '@labcraft/core'
import { z } from 'zod'
import { MachineLifecycleStateSchema } from './hypervisor_routes.ts'

////////////////////////////////////////////////////////////////////////////////
// Control envelopes (client → server)
////////////////////////////////////////////////////////////////////////////////

/**
 * Resume envelope — auto-sent by the framework's `WsSubscription` on
 * every reconnect after the first. The read-only proxy ignores it (it
 * forwards nothing upstream); the type exists so the typed client's
 * outbound validation accepts the framework's send.
 *
 * @property type - Literal `'resume'` (discriminant).
 */
export const AdminTerminalResumeFrameSchema = z.object({
  type: z.literal('resume'),
})

/** Parsed `resume` envelope. */
export type AdminTerminalResumeFrame = z.infer<
  typeof AdminTerminalResumeFrameSchema
>

/**
 * Every text frame the browser may send. Read-only: only the
 * framework's auto-`resume`. Kept a discriminated union so a future
 * interactive mode adds a `resize` variant without a wire-shape break.
 */
export const AdminTerminalClientFrameSchema = z.discriminatedUnion('type', [
  AdminTerminalResumeFrameSchema,
])

/** Parsed text frame the browser sends. */
export type AdminTerminalClientFrame = z.infer<
  typeof AdminTerminalClientFrameSchema
>

////////////////////////////////////////////////////////////////////////////////
// Server-push envelopes (server → client)
////////////////////////////////////////////////////////////////////////////////

/**
 * Lifecycle frame — mirrors the hypervisor's pty lifecycle push. Drives
 * the panel's state pill. `expiresAt` is never stamped by admin (no
 * engagement TTL on the operator side); kept optional to match the
 * upstream wire shape.
 *
 * @property type - Literal `'lifecycle'` (discriminant).
 * @property state - Current machine lifecycle state.
 * @property expiresAt - Engagement-TTL deadline (ms since epoch);
 *   absent on the admin side.
 */
export const AdminTerminalLifecycleFrameSchema = z.object({
  type: z.literal('lifecycle'),
  state: MachineLifecycleStateSchema,
  expiresAt: z.number().int().positive().optional(),
})

/** Parsed lifecycle frame. */
export type AdminTerminalLifecycleFrame = z.infer<
  typeof AdminTerminalLifecycleFrameSchema
>

/**
 * Resize frame — the guest pty's current window size. Relayed from the
 * hypervisor's server-side `resize` push so the admin terminal mirrors
 * the learner's dimensions (and the drawer resizes to fit).
 *
 * @property type - Literal `'resize'` (discriminant).
 * @property payload - Cols/rows of the learner's pty window; positive
 *   integers.
 */
export const AdminTerminalResizeFrameSchema = z.object({
  type: z.literal('resize'),
  payload: z.object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
  }),
})

/** Parsed resize frame. */
export type AdminTerminalResizeFrame = z.infer<
  typeof AdminTerminalResizeFrameSchema
>

/**
 * Every text frame the proxy pushes to the browser: the lifecycle
 * state and the winsize mirror.
 */
export const AdminTerminalServerFrameSchema = z.discriminatedUnion('type', [
  AdminTerminalLifecycleFrameSchema,
  AdminTerminalResizeFrameSchema,
])

/** Parsed text frame the proxy pushes to the browser. */
export type AdminTerminalServerFrame = z.infer<
  typeof AdminTerminalServerFrameSchema
>

////////////////////////////////////////////////////////////////////////////////
// Path params
////////////////////////////////////////////////////////////////////////////////

/**
 * Path params for the admin terminal WS. The operator names the machine
 * by its hypervisor `id` directly (straight from the machine list) — no
 * engagement/spec-hash derivation as on the learner side.
 *
 * @property id - The hypervisor Machine id to observe.
 */
export const AdminTerminalSubParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed path params. */
export type AdminTerminalSubParams = z.infer<
  typeof AdminTerminalSubParamsSchema
>

////////////////////////////////////////////////////////////////////////////////
// AdminTerminalSub contract
////////////////////////////////////////////////////////////////////////////////

/**
 * `WS /api/hypervisor/:id/terminal` — admin's read-only terminal
 * observer. The SPA dials it via `ws.subscribe(AdminTerminalSub, {
 * params: { id } })`; the admin service binds the same contract via
 * `bindWebSocket` and bridges (read-only) to the hypervisor's pty WS.
 *
 * Binary frames are pty stdout bytes, one direction only (server →
 * browser); the proxy drops browser-side binary and resize.
 */
export const AdminTerminalSub = defineSubscription({
  path: '/api/hypervisor/:id/terminal',
  params: AdminTerminalSubParamsSchema,
  clientFrame: AdminTerminalClientFrameSchema,
  serverFrame: AdminTerminalServerFrameSchema,
  description: CoreString.oneLine`
    Read-only terminal observer. Browser dials admin; admin proxies to the
    hypervisor pty over WG (loopback in dev). Server → browser: pty stdout
    bytes (binary) plus lifecycle + resize text frames. Browser → server is
    dropped (the operator observes, never types).
  `,
})
